/* eslint-disable react/no-unescaped-entities */
/* Transport — Multi-carrier freight rate comparison & booking. */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Truck, Search, Loader2, Package as PkgIcon, ExternalLink, Settings as SettingsIcon, Plus, X, ListChecks, Zap, MapPin } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_ADDR = {
    company: '', contact_name: '', street_name: '', street_number: '',
    zipcode: '', city: '', country: 'NL', phone: '+31', email: '',
};
const EMPTY_ITEM = { type: 'PALLET', length_cm: 80, width_cm: 120, height_cm: 120, count: 1 };

export default function Transport() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('quick'); // quick | quote | shipments

    return (
        <DashboardLayout>
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-[1400px] mx-auto px-3 md:px-6 py-3 md:py-5">
                    <div className="flex items-center gap-3 mb-4">
                        <Truck size={22} className="text-gray-700" />
                        <h1 className="text-lg md:text-xl font-bold text-gray-900">Transport</h1>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg mb-3 overflow-x-auto">
                        <div className="flex gap-1 p-1 min-w-max">
                            {[
                                { k: 'quick', l: 'Hızlı Fiyat', icon: Zap },
                                { k: 'quote', l: 'Detaylı Sorgu', icon: Search },
                                { k: 'shipments', l: 'Gönderiler', icon: ListChecks },
                            ].map((t) => {
                                const Icon = t.icon;
                                const active = tab === t.k;
                                return (
                                    <button key={t.k} onClick={() => setTab(t.k)}
                                        className={`flex items-center gap-1.5 px-3 md:px-4 py-2 text-xs md:text-sm font-semibold rounded-md whitespace-nowrap transition-colors ${
                                            active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        data-testid={`transport-tab-${t.k}`}>
                                        <Icon size={14} />{t.l}
                                    </button>
                                );
                            })}
                            <button onClick={() => navigate('/settings?tab=api')}
                                className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs md:text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md"
                                data-testid="transport-settings-link">
                                <SettingsIcon size={14} /> Ayarlar
                            </button>
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-5">
                        {tab === 'quick' && <QuickQuote />}
                        {tab === 'quote' && <QuoteForm />}
                        {tab === 'shipments' && <ShipmentsList onJumpToQuote={() => setTab('quote')} />}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

/* ─── HIZLI FIYAT (preset şehirler + palet sayısı / FTL) ──────────────────── */

const PRESET_CITIES = [
    { id: 'rotterdam', name: 'Rotterdam', country: 'NL', zip: '3011AD', city: 'Rotterdam' },
    { id: 'amsterdam', name: 'Amsterdam', country: 'NL', zip: '1011AB', city: 'Amsterdam' },
    { id: 'antwerpen', name: 'Antwerpen', country: 'BE', zip: '2000', city: 'Antwerpen' },
    { id: 'brussel', name: 'Brüksel', country: 'BE', zip: '1000', city: 'Bruxelles' },
    { id: 'paris', name: 'Paris', country: 'FR', zip: '75001', city: 'Paris' },
    { id: 'lyon', name: 'Lyon', country: 'FR', zip: '69001', city: 'Lyon' },
    { id: 'marseille', name: 'Marsilya', country: 'FR', zip: '13001', city: 'Marseille' },
    { id: 'koln', name: 'Köln', country: 'DE', zip: '50667', city: 'Köln' },
    { id: 'dortmund', name: 'Dortmund', country: 'DE', zip: '44135', city: 'Dortmund' },
    { id: 'hamburg', name: 'Hamburg', country: 'DE', zip: '20095', city: 'Hamburg' },
    { id: 'munchen', name: 'Münih', country: 'DE', zip: '80331', city: 'München' },
    { id: 'wien', name: 'Viyana', country: 'AT', zip: '1010', city: 'Wien' },
];

const FLAG = { NL: '🇳🇱', BE: '🇧🇪', FR: '🇫🇷', DE: '🇩🇪', AT: '🇦🇹' };

function QuickQuote() {
    const navigate = useNavigate();
    const [pickup, setPickup] = useState(null);
    const [destCityId, setDestCityId] = useState('rotterdam');
    const [palletCount, setPalletCount] = useState(1);
    const [ftl, setFtl] = useState(false);
    const [weightPerPallet, setWeightPerPallet] = useState(300);
    const [stackable, setStackable] = useState(false);
    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [bookingProvider, setBookingProvider] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        axios.get(`${API}/api/transport/config`, { headers: hdrs() })
            .then((r) => setPickup(r.data?.default_pickup || null))
            .catch(() => setPickup(null));
    }, []);

    const dest = PRESET_CITIES.find(c => c.id === destCityId);
    const effectiveCount = ftl ? 33 : palletCount;
    const totalWeight = effectiveCount * weightPerPallet;

    const buildPayload = () => {
        const deliveryAddr = {
            company: '',
            contact_name: '',
            street_name: 'Centraal',
            street_number: '1',
            zipcode: dest.zip,
            city: dest.city,
            country: dest.country,
            phone: '+31',
            email: '',
        };
        return {
            cargo_type: 'OTHER',
            total_weight_kg: totalWeight,
            closed_box: true,
            stackable,
            is_b2c: false,
            notes: ftl ? 'Full Truckload (FTL) request' : `${effectiveCount} palet — hızlı fiyat sorgu`,
            pickup: pickup || { ...EMPTY_ADDR },
            delivery: deliveryAddr,
            items: [{
                type: 'EUROPALLET',
                length_cm: 80,
                width_cm: 120,
                height_cm: 140,
                count: effectiveCount,
            }],
        };
    };

    const fetchQuote = async () => {
        setError(null);
        if (!pickup || !pickup.zipcode) {
            setError('Önce Ayarlar → Transport API\'ları altından varsayılan yükleme adresini girin.');
            return;
        }
        setLoading(true);
        setQuotes([]);
        try {
            const r = await axios.post(`${API}/api/transport/quote`, buildPayload(), { headers: hdrs() });
            setQuotes(r.data?.quotes || []);
        } catch (e) {
            setError(_errMsg(e, 'Sorgulama hatası'));
        } finally {
            setLoading(false);
        }
    };

    const book = async (q) => {
        if (!window.confirm(`${q.provider.toUpperCase()} ile siparişi göndermek istediğinden emin misin? (€${fmt(q.price_excl_vat)})`)) return;
        setBookingProvider(q.provider);
        try {
            const bookPayload = { ...buildPayload(), provider: q.provider };
            if (q.quotation_id) bookPayload.quotation_id = q.quotation_id;
            const r = await axios.post(`${API}/api/transport/book`, bookPayload, { headers: hdrs() });
            toast.success(`Sipariş oluşturuldu — ${q.provider} (#${r.data?.provider_order_id || '?'})`);
            setQuotes([]);
        } catch (e) {
            toast.error(_errMsg(e, 'Sipariş hatası'));
        } finally {
            setBookingProvider(null);
        }
    };

    const best = quotes.filter(q => q.ok).sort((a, b) => (a.price_excl_vat || 0) - (b.price_excl_vat || 0))[0];

    return (
        <div className="space-y-5" data-testid="quick-quote-form">
            {/* Origin info */}
            <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
                <MapPin size={18} className="text-blue-600 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Yükleme Adresi (varsayılan)</div>
                    {pickup && pickup.zipcode ? (
                        <div className="text-sm font-semibold text-gray-900 truncate">
                            {FLAG[pickup.country] || ''} {pickup.zipcode} {pickup.city}
                            {pickup.street_name && <span className="text-gray-500 font-normal"> · {pickup.street_name} {pickup.street_number}</span>}
                        </div>
                    ) : (
                        <div className="text-xs text-amber-700">Varsayılan adres yok — Ayarlar'dan ekle</div>
                    )}
                </div>
                <button onClick={() => navigate('/settings?tab=api')}
                    className="h-8 px-3 text-xs font-semibold text-blue-700 hover:bg-blue-100 rounded-md whitespace-nowrap"
                    data-testid="quick-edit-pickup">
                    Düzenle
                </button>
            </div>

            {/* Destination */}
            <div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2 flex items-center gap-2">
                    <MapPin size={12} /> Teslimat Şehri
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-1.5">
                    {PRESET_CITIES.map(c => {
                        const active = destCityId === c.id;
                        return (
                            <button key={c.id} onClick={() => setDestCityId(c.id)}
                                className={`flex flex-col items-center gap-0.5 py-2 px-1 rounded-md text-xs font-semibold border transition-all ${
                                    active
                                        ? 'bg-gray-900 text-white border-gray-900 shadow-sm'
                                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                                }`}
                                data-testid={`city-${c.id}`}>
                                <span className="text-base leading-none">{FLAG[c.country]}</span>
                                <span className="leading-tight">{c.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Pallet count or FTL */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Yük Miktarı</div>
                    <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                        <input type="checkbox" checked={ftl} onChange={(e) => setFtl(e.target.checked)}
                            data-testid="ftl-checkbox" className="h-4 w-4" />
                        <span className={ftl ? 'text-gray-900' : 'text-gray-500'}>Full Truckload (FTL · 33 palet)</span>
                    </label>
                </div>

                {!ftl && (
                    <div className="space-y-2">
                        <div className="grid grid-cols-6 sm:grid-cols-11 gap-1">
                            {Array.from({ length: 33 }, (_, i) => i + 1).map(n => {
                                const active = palletCount === n;
                                return (
                                    <button key={n} onClick={() => setPalletCount(n)}
                                        className={`h-9 text-xs font-bold rounded transition-all ${
                                            active
                                                ? 'bg-blue-600 text-white shadow-md scale-105'
                                                : n <= 5 ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                                                : n <= 15 ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                                : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                        }`}
                                        data-testid={`pallet-${n}`}>
                                        {n}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="text-[11px] text-gray-500">
                            🟢 1-5 palet · 🟡 6-15 palet · ⚪ 16-32 palet · Üzeri için FTL seçin
                        </div>
                    </div>
                )}

                {ftl && (
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-lg flex items-center gap-3">
                        <Truck size={28} className="text-purple-600" />
                        <div>
                            <div className="text-sm font-bold text-purple-900">Komple TIR (Full Truckload)</div>
                            <div className="text-xs text-purple-700">33 europalet kapasite · ~24 ton max · 13.6m kasa</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Weight & options */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
                            Palet Başı Ağırlık (kg)
                        </span>
                        <input type="number" min="1" max="2000" value={weightPerPallet}
                            onChange={(e) => setWeightPerPallet(Math.max(1, Number(e.target.value) || 0))}
                            className="mt-1 w-full h-9 px-2 text-sm border border-gray-300 rounded-md"
                            data-testid="weight-per-pallet" />
                    </label>
                </div>
                <div className="flex flex-col justify-end">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Toplam Ağırlık</div>
                    <div className="h-9 flex items-center text-base font-bold text-gray-900">
                        {totalWeight.toLocaleString('de-DE')} kg
                    </div>
                </div>
                <div className="flex flex-col justify-end">
                    <label className="flex items-center gap-2 text-xs h-9">
                        <input type="checkbox" checked={stackable} onChange={(e) => setStackable(e.target.checked)}
                            data-testid="quick-stackable" />
                        <span>İstiflenebilir (stackable)</span>
                    </label>
                </div>
            </div>

            {/* Route preview & submit */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1 flex items-center gap-2 text-sm min-w-0">
                    {pickup?.city && (
                        <span className="font-semibold whitespace-nowrap">
                            {FLAG[pickup.country] || ''} {pickup.city}
                        </span>
                    )}
                    <span className="text-gray-400">→</span>
                    <span className="font-semibold whitespace-nowrap">{FLAG[dest.country]} {dest.name}</span>
                    <span className="text-gray-300 mx-2">·</span>
                    <span className="text-gray-600 whitespace-nowrap">{effectiveCount} palet · {totalWeight} kg</span>
                </div>
                <button onClick={fetchQuote} disabled={loading || !pickup?.zipcode}
                    className="h-11 px-6 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 rounded-md flex items-center gap-2 shadow-md whitespace-nowrap"
                    data-testid="quick-fetch-btn">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                    {loading ? 'Sorgulanıyor...' : 'Fiyat Al'}
                </button>
            </div>

            {error && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900" data-testid="quick-error">
                    {error}
                </div>
            )}

            {/* Results */}
            {quotes.length > 0 && (
                <div className="pt-3 border-t" data-testid="quick-results">
                    <h3 className="text-sm font-bold mb-3">
                        Teklifler · <span className="text-emerald-700">{quotes.filter(q => q.ok).length} başarılı</span>
                    </h3>
                    <div className="space-y-2">
                        {quotes.sort((a, b) => (b.ok ? 1 : 0) - (a.ok ? 1 : 0) || (a.price_excl_vat || 999999) - (b.price_excl_vat || 999999)).map((q, i) => {
                            const isBest = best && q.provider === best.provider;
                            return (
                                <div key={i}
                                    className={`relative p-3 rounded-lg border flex items-center justify-between gap-3 ${
                                        !q.ok ? 'bg-gray-50 border-gray-200'
                                        : isBest ? 'bg-gradient-to-r from-emerald-50 to-cyan-50 border-emerald-400 shadow-md'
                                        : 'bg-emerald-50 border-emerald-200'
                                    }`}
                                    data-testid={`quick-quote-${q.provider}`}>
                                    {isBest && (
                                        <span className="absolute -top-2 left-3 text-[9px] font-black tracking-wider px-2 py-0.5 bg-emerald-600 text-white rounded-full">
                                            EN UYGUN
                                        </span>
                                    )}
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs ${q.ok ? 'bg-emerald-600' : 'bg-gray-400'}`}>
                                            {q.provider.slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-sm font-bold capitalize">{q.provider}</div>
                                            {q.ok ? (
                                                <div className="text-xs text-emerald-700">
                                                    {q.offer_type} · Net (BTW hariç)
                                                    {q.runtime_days && ` · ${q.runtime_days} gün`}
                                                </div>
                                            ) : (
                                                <div className="text-xs text-rose-600 truncate" title={q.error}>{q.error}</div>
                                            )}
                                        </div>
                                    </div>
                                    {q.ok && (
                                        <>
                                            <div className="text-2xl font-black text-emerald-700 tabular-nums whitespace-nowrap">
                                                €{fmt(q.price_excl_vat)}
                                            </div>
                                            <button onClick={() => book(q)} disabled={bookingProvider === q.provider}
                                                className="h-10 px-4 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 rounded-md whitespace-nowrap"
                                                data-testid={`quick-book-${q.provider}`}>
                                                {bookingProvider === q.provider ? 'Gönderiliyor...' : 'Sipariş Ver'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}



function QuoteForm() {
    const [pickup, setPickup] = useState({ ...EMPTY_ADDR });
    const [delivery, setDelivery] = useState({ ...EMPTY_ADDR });
    const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
    const [weight, setWeight] = useState(30);
    const [cargoType, setCargoType] = useState('OTHER');
    const [notes, setNotes] = useState('Generic cargo');
    const [closedBox, setClosedBox] = useState(true);
    const [stackable, setStackable] = useState(false);
    const [isB2C, setIsB2C] = useState(false);
    const [loading, setLoading] = useState(false);
    const [quotes, setQuotes] = useState([]);
    const [bookingProvider, setBookingProvider] = useState(null);

    // Load default pickup on mount
    useEffect(() => {
        axios.get(`${API}/api/transport/config`, { headers: hdrs() })
            .then((r) => {
                const dp = r.data?.default_pickup;
                if (dp && Object.keys(dp).length > 0) {
                    setPickup({ ...EMPTY_ADDR, ...dp });
                }
            })
            .catch(() => {});
    }, []);

    const buildPayload = () => ({
        cargo_type: cargoType,
        total_weight_kg: Number(weight) || 0,
        closed_box: closedBox,
        stackable,
        is_b2c: isB2C,
        notes,
        pickup,
        delivery,
        items,
    });

    const fetchQuote = async () => {
        if (!pickup.zipcode || !delivery.zipcode) {
            toast.error('Pickup ve teslim posta kodu zorunlu');
            return;
        }
        setLoading(true);
        setQuotes([]);
        try {
            const r = await axios.post(`${API}/api/transport/quote`, buildPayload(), { headers: hdrs() });
            setQuotes(r.data?.quotes || []);
        } catch (e) {
            toast.error(_errMsg(e, 'Sorgulama hatasi'));
        } finally {
            setLoading(false);
        }
    };

    const book = async (q) => {
        if (!window.confirm(`${q.provider.toUpperCase()} ile siparisi gondermek istediğinden emin misin? (€${fmt(q.price_excl_vat)})`)) return;
        setBookingProvider(q.provider);
        try {
            const bookPayload = { ...buildPayload(), provider: q.provider };
            if (q.quotation_id) bookPayload.quotation_id = q.quotation_id;
            const r = await axios.post(`${API}/api/transport/book`, bookPayload, { headers: hdrs() });
            toast.success(`Siparis olusturuldu — ${q.provider} (#${r.data?.provider_order_id || '?'})`);
            setQuotes([]);
        } catch (e) {
            toast.error(_errMsg(e, 'Siparis hatasi'));
        } finally {
            setBookingProvider(null);
        }
    };

    return (
        <div className="space-y-4" data-testid="transport-quote-form">
            {/* Cargo info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Yuk Tipi">
                    <select value={cargoType} onChange={(e) => setCargoType(e.target.value)}
                        className="w-full h-9 px-2 text-sm border border-gray-300 rounded-md"
                        data-testid="cargo-type-select">
                        {['OTHER', 'DRYFOOD', 'TEXTILE', 'PLASTIC', 'ELECTRONICS', 'FRAGILE', 'METAL', 'WOOD'].map(t =>
                            <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
                <Field label="Toplam Agirlik (kg)">
                    <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)}
                        className="w-full h-9 px-2 text-sm border border-gray-300 rounded-md"
                        data-testid="cargo-weight-input" />
                </Field>
                <Field label="Aciklama">
                    <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="w-full h-9 px-2 text-sm border border-gray-300 rounded-md" />
                </Field>
                <div className="flex items-end gap-3 text-xs">
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={closedBox}
                        onChange={(e) => setClosedBox(e.target.checked)} /> Kapali Kasa</label>
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={stackable}
                        onChange={(e) => setStackable(e.target.checked)} /> Istiflenebilir</label>
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={isB2C}
                        onChange={(e) => setIsB2C(e.target.checked)} /> B2C</label>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AddressBlock title="Yukleme (Pickup)" addr={pickup} setAddr={setPickup} testPrefix="pickup" />
                <AddressBlock title="Teslim (Delivery)" addr={delivery} setAddr={setDelivery} testPrefix="delivery" />
            </div>

            {/* Items */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Pallet / Paket</div>
                    <button onClick={() => setItems([...items, { ...EMPTY_ITEM }])}
                        className="h-7 px-2.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md flex items-center gap-1"
                        data-testid="add-item-btn">
                        <Plus size={12} /> Ekle
                    </button>
                </div>
                <div className="space-y-2">
                    {items.map((it, i) => (
                        <div key={i} className="grid grid-cols-2 md:grid-cols-6 gap-2 items-center bg-gray-50 p-2 rounded-md" data-testid={`item-row-${i}`}>
                            <select value={it.type} onChange={(e) => updateItem(items, setItems, i, 'type', e.target.value)}
                                className="h-8 px-2 text-xs border border-gray-300 rounded">
                                {['PALLET', 'BOX', 'EUROPALLET'].map(t => <option key={t}>{t}</option>)}
                            </select>
                            <NumIn v={it.length_cm} onChange={(v) => updateItem(items, setItems, i, 'length_cm', v)} ph="L cm" />
                            <NumIn v={it.width_cm} onChange={(v) => updateItem(items, setItems, i, 'width_cm', v)} ph="W cm" />
                            <NumIn v={it.height_cm} onChange={(v) => updateItem(items, setItems, i, 'height_cm', v)} ph="H cm" />
                            <NumIn v={it.count} onChange={(v) => updateItem(items, setItems, i, 'count', v)} ph="Adet" />
                            <button onClick={() => setItems(items.filter((_, idx) => idx !== i))} disabled={items.length === 1}
                                className="h-8 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-30">
                                <X size={14} className="mx-auto" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Submit */}
            <div className="flex items-center justify-end gap-2 pt-2">
                <button onClick={fetchQuote} disabled={loading}
                    className="h-10 px-5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md flex items-center gap-2"
                    data-testid="get-quote-btn">
                    {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                    {loading ? 'Sorgulanıyor...' : 'Fiyat Sorgula'}
                </button>
            </div>

            {/* Quote results */}
            {quotes.length > 0 && (
                <div className="pt-3 border-t" data-testid="quote-results">
                    <h3 className="text-sm font-bold mb-2">Teklifler ({quotes.filter(q => q.ok).length} basarili)</h3>
                    <div className="space-y-2">
                        {quotes.map((q, i) => (
                            <div key={i} className={`p-3 rounded-lg border flex items-center justify-between gap-3 ${q.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}
                                data-testid={`quote-${q.provider}`}>
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs ${q.ok ? 'bg-emerald-600' : 'bg-gray-400'}`}>
                                        {q.provider.slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-bold capitalize">{q.provider}</div>
                                        {q.ok
                                            ? <div className="text-xs text-emerald-700">
                                                {q.offer_type} · Net (BTW haric)
                                                {q.runtime_days && ` · ${q.runtime_days} gun`}
                                              </div>
                                            : <div className="text-xs text-rose-600 truncate" title={q.error}>{q.error}</div>}
                                    </div>
                                </div>
                                {q.ok && (
                                    <>
                                        <div className="text-xl font-bold text-emerald-700 tabular-nums whitespace-nowrap">
                                            €{fmt(q.price_excl_vat)}
                                        </div>
                                        <button onClick={() => book(q)} disabled={bookingProvider === q.provider}
                                            className="h-9 px-3 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 rounded-md whitespace-nowrap"
                                            data-testid={`book-${q.provider}`}>
                                            {bookingProvider === q.provider ? 'Gonderiliyor...' : 'Siparis Ver'}
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function ShipmentsList() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        axios.get(`${API}/api/transport/shipments`, { headers: hdrs() })
            .then((r) => setItems(r.data?.items || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-sm text-gray-500 py-4">Yukleniyor...</div>;
    if (!items.length) {
        return (
            <div className="text-center py-10">
                <PkgIcon size={36} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">Henuz gonderi yok</p>
            </div>
        );
    }

    return (
        <div className="space-y-2" data-testid="shipments-list">
            {items.map((s) => (
                <div key={s.id} className="p-3 border border-gray-200 rounded-lg bg-white" data-testid={`shipment-${s.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                            <div className="text-sm font-semibold capitalize">{s.provider} · #{s.provider_order_id}</div>
                            <div className="text-[11px] text-gray-500">{s.created_at?.slice(0, 16).replace('T', ' ')}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-sm font-bold tabular-nums">€{fmt(s.price_excl_vat)}</div>
                            <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{s.status}</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                        <div>
                            <span className="text-gray-400">Yukleme: </span>
                            {s.pickup?.city}, {s.pickup?.country}
                        </div>
                        <div>
                            <span className="text-gray-400">Teslim: </span>
                            {s.delivery?.city}, {s.delivery?.country}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function AddressBlock({ title, addr, setAddr, testPrefix }) {
    const upd = (k, v) => setAddr({ ...addr, [k]: v });
    return (
        <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">{title}</div>
            <div className="grid grid-cols-2 gap-2">
                <Field label="Sirket"><input value={addr.company} onChange={(e) => upd('company', e.target.value)}
                    className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-company`} /></Field>
                <Field label="Kontak Adi"><input value={addr.contact_name} onChange={(e) => upd('contact_name', e.target.value)}
                    className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-contact`} /></Field>
                <Field label="Cadde"><input value={addr.street_name} onChange={(e) => upd('street_name', e.target.value)}
                    className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-street`} /></Field>
                <Field label="No"><input value={addr.street_number} onChange={(e) => upd('street_number', e.target.value)}
                    className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-streetno`} /></Field>
                <Field label="Posta Kodu"><input value={addr.zipcode} onChange={(e) => upd('zipcode', e.target.value)}
                    className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-zip`} /></Field>
                <Field label="Sehir"><input value={addr.city} onChange={(e) => upd('city', e.target.value)}
                    className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-city`} /></Field>
                <Field label="Ulke">
                    <select value={addr.country} onChange={(e) => upd('country', e.target.value)}
                        className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-country`}>
                        {['NL', 'BE', 'DE', 'FR', 'LU', 'IT', 'ES', 'PL', 'GB'].map(c => <option key={c}>{c}</option>)}
                    </select>
                </Field>
                <Field label="Telefon"><input value={addr.phone} onChange={(e) => upd('phone', e.target.value)}
                    className="w-full h-8 px-2 text-xs border border-gray-300 rounded" data-testid={`${testPrefix}-phone`} /></Field>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
            {children}
        </label>
    );
}

function NumIn({ v, onChange, ph }) {
    return (
        <input type="number" value={v} onChange={(e) => onChange(Number(e.target.value) || 0)} placeholder={ph}
            className="h-8 px-2 text-xs border border-gray-300 rounded text-right tabular-nums" />
    );
}

function updateItem(items, setItems, idx, key, val) {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [key]: val };
    setItems(copy);
}

function _errMsg(e, fallback) {
    const d = e?.response?.data?.detail;
    if (!d) return fallback;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(', ');
    return JSON.stringify(d);
}
